const AppPath = require('@/lib/Navigation/AppPath');
const CartLine = require('@/lib/Cart/CartLine');
const ItemType = require('@/lib/Items/ItemType');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { ANGULAR_MODALS } = require('@/lib/Modals/ANGULAR_MODALS');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');
const { suppressModalCancelledErrorOrReThrow } = require('@/lib/Errors');
const { updateAngularUi } = require('@/functions/angular');

/**
 * Provides common methods used by OrderDetailsCtrl, MembershipDetailsCtrl, and AccountDetailsView.
 * for viewing/updating details about Memberships.
 * The aim is to reduce duplication of code that opens modals / does other things for modifying
 * things about memberships.
 *
 * @constructor
 *
 * @param $filter
 * @param $q
 * @param $location
 * @param $modal
 * @param $timeout
 * @param {CartService} cartService
 * @param {DialogService} dialogService
 * @param {Hydrator} hydrator
 * @param {LangService} langService
 * @param {MembershipService} membershipService
 * @param {ModalFactory} modalFactory
 * @param {NavigationService} navigationService
 * @param {ToastFactory} toastFactory
 * @param {UserService} userService
 * @param {VueModalFactory} vueModalFactory
 */
const MembershipManagementService = function (
    $filter,
    $q,
    $location,
    $modal,
    $timeout,
    cartService,
    dialogService,
    hydrator,
    langService,
    membershipService,
    modalFactory,
    navigationService,
    toastFactory,
    userService,
    vueModalFactory
) {
    this.$filter = $filter;
    this.$q = $q;
    this.$location = $location;
    this.$modal = $modal;
    this.$timeout = $timeout;
    this.userService = userService;
    this.vueModalFactory = vueModalFactory;

    /**
     * @type {CartService}
     */
    this.cartService = cartService;

    /**
     * @type {DialogService}
     */
    this.dialogService = dialogService;

    /**
     * @type {Hydrator}
     */
    this.hydrator = hydrator;

    /**
     * @type {LangService}
     */
    this.langService = langService;

    this.logger = logFactory('MembershipManagementService');

    /**
     * @type {MembershipService}
     */
    this.membershipService = membershipService;

    /**
     * @type {ModalFactory}
     */
    this.modalFactory = modalFactory;

    /**
     * @type {NavigationService}
     */
    this.navigationService = navigationService;

    /**
     * Is the renewal modal open?
     *
     * @type {boolean}
     */
    this.renewalDateModalOpen = false;

    /**
     * @type {ToastFactory}
     */
    this.toastFactory = toastFactory;
};

MembershipManagementService.prototype = {

    /**
     * Display the membership datasets modal to edit one or more datasets.
     * Returns the modified datasets in the callback (these are clones, not the same object that are passed in).
     * This does not persist the changes on the server or elsewhere. It is up to the calling code to do that.
     *
     * @param {string} action
     * @param {MembershipPlan} membershipPlan
     * @param {MembershipDataset[]} membershipDatasets
     * @param {CustomerAccount} customerAccount
     * @param {function} callback
     */
    editDatasets: function editDatasets(
        action,
        membershipPlan,
        membershipDatasets,
        customerAccount,
        callback
    ) {
        // This is the user adding/editing the item, so show the pop-up.
        this.logger.debug(
            'Sell.ShowMembershipDatasetsModal',
            {
                planID: membershipPlan.ID,
                mode: (action === 'add-new' ? 'Add membership' : 'Edit memberships'),
            }
        );

        this.modalFactory
            .openFromData(
                ANGULAR_MODALS.MEMBERSHIP_DATASETS,
                {
                    action: () => action,
                    membershipPlan: () => membershipPlan,
                    currentMemberID: () => null,
                    customerAccount: () => customerAccount,
                    membershipDatasets: () => membershipDatasets,
                }
            )
            .then(
                function success(data) {
                    callback(data.membershipDatasets);
                },
                function error() {
                    callback(null);
                }
            );
    },

    /**
     * Opens the modal to select a refund amount for each selected membership.
     *
     * The modal returns some cart items to do those refunds, each item has the cancellingMembership property set.
     * We then create a SellState and send the user to the SellCtrl.
     * The SellCtrl will call the MembershipCancellationRunner to do the actual cancellation when the user clicks
     * the pay button. (But we also tell it to 'click' the pay button as soon as it loads.)
     *
     * The SellCtrl handles the cancellation when the pay button is clicked because that is a better time for it
     * to happen when modifying a membership (cancelling and selling a new one in the same order), and it is
     * best to be consistent with when the actual cancellation happens.
     *
     * @param {Membership[]} memberships
     * @param {string} returnPathString
     * @param {CustomerAccount|undefined} [customerAccount]
     */
    cancelMemberships: function cancelMemberships(memberships, returnPathString, customerAccount) {
        if (memberships.length < 1) {
            return;
        }

        // Determine the account from a membership.
        // All memberships being deleted at the same time should be from the same account,
        // otherwise you're doing something weird!
        if (customerAccount === undefined) {
            customerAccount = null;
            for (let i = 0; i < memberships.length; i++) {
                if (memberships[i].customerAccount) {
                    customerAccount = memberships[i].customerAccount;
                    break;
                }
            }
        }

        this.logger.debug(
            'Manage.ShowCancelMembershipsModal',
            {
                membershipIDs: this.membershipService.mapMembershipIds(memberships),
            }
        );

        // Ask the user what refund amounts they want to give.
        this.vueModalFactory
            .open(
                MODAL_NAMES__EPOS.MEMBERSHIP_CANCELLATION,
                {
                    memberships,
                    mode: 'cancel',
                }
            )
            .then(async (response) => {
                this.logger.debug(
                    'Manage.SubmitCancelMembershipsModal',
                    {
                        membershipIDs: this.membershipService.mapMembershipIds(memberships),
                    }
                );

                if (response.refundCartItems.length > 0) {
                    // Send user to sell screen to issue refunds.
                    const cart = this.cartService.createEmptyCart();
                    response.refundCartItems.forEach((item) => {
                        cart.addItem(item);
                    });

                    cart.sellState.payNow = true;
                    cart.sellState.preventAdditional = true;
                    cart.sellState.preventRemoval = true;
                    cart.sellState.preventQtyAdjustment = true;
                    cart.customer.account = customerAccount;

                    const returnPath = new AppPath(returnPathString);
                    returnPath.params = { r: 1 };
                    cart.sellState.cancelCartReturnPath = returnPath;
                    cart.sellState.cancelPaymentReturnPath = returnPath;
                    cart.sellState.successReturnPath = returnPath;

                    const cartSet = await this.cartService.setNewCart(cart, false);
                    if (cartSet) {
                        this.navigationService.viewSell();
                    }
                }
            })
            .catch(() => {
                // Cancelled
                this.logger.debug(
                    'Manage.DismissCancelMembershipsModal',
                    {
                        membershipIDs: this.membershipService.mapMembershipIds(memberships),
                    }
                );
            });
    },

    /**
     * For one or more memberships:
     * - Asks the user how much they want to refund for each membership
     *   (to be used as credit towards the next membership).
     * - Adds those refunds to the cart.
     * - Sends the user to the cart with those refunds and only allows them to add replacement
     *   memberships to the cart.
     *
     * @param {Membership[]} memberships
     * @param {string} returnPathString
     * @param {CustomerAccount} customerAccount
     */
    changeMembershipPlans(memberships, returnPathString, customerAccount) {
        if (memberships.length < 1) {
            return;
        }

        // Ask the user what refund amounts they want to give.
        this.vueModalFactory
            .open(
                MODAL_NAMES__EPOS.MEMBERSHIP_CANCELLATION,
                {
                    memberships,
                    mode: 'change',
                }
            )
            .then(async (response) => {
                if (response.refundCartItems.length > 0) {
                    // Send user to sell screen to sell replacement memberships.
                    const cart = this.cartService.createEmptyCart();
                    response.refundCartItems.forEach((item) => {
                        cart.addItem(item);
                    });
                    cart.sellState.restrictItemTypes = [
                        ItemType.MEMBERSHIP_PLAN,
                    ];
                    cart.customer.account = customerAccount;

                    const returnPath = new AppPath(returnPathString);
                    returnPath.params = { r: 1 };
                    cart.sellState.cancelCartReturnPath = returnPath;
                    cart.sellState.cancelPaymentReturnPath = returnPath;
                    cart.sellState.successReturnPath = returnPath;

                    const cartSet = await this.cartService.setNewCart(cart, false);
                    if (cartSet) {
                        this.navigationService.viewSell();
                    }
                }
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    },

    /**
     * Open the modal to change the subscription installment amounts for a membership.
     * The modal persists the changes via the API when it is submitted.
     *
     * @param {Membership} membership
     */
    changeInstallments(membership) {
        this.logger.debug(
            'Manage.ShowChangeInstallmentsModal',
            {
                membershipID: membership.getID(),
            }
        );

        this.vueModalFactory
            .open(
                MODAL_NAMES__EPOS.MEMBERSHIP_INSTALLMENTS,
                {
                    membership,
                }
            )
            .then((result) => {
                this.logger.debug(
                    'Manage.UpdateInstallmentAmounts',
                    {
                        membershipID: membership.getID(),
                        success: result.success,
                        newInstallmentAmounts: result.newInstallmentAmounts,
                    }
                );

                if (result.success) {
                    this.toastFactory.success('MEMBERSHIP_INSTALLMENTS.CHANGES_SAVED');
                } else {
                    this.toastFactory.errorTop('MEMBERSHIP_INSTALLMENTS.CHANGES_NOT_SAVED');
                }
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    },

    /**
     * Set one or more memberships to no longer automatically renew.
     * Persists the changes via the API and displays a notification with the result.
     *
     * @param {Membership[]} memberships
     * @param {function} callback
     */
    stopRenewingMemberships: function stopRenewingMemberships(memberships, callback) {
        if (memberships.length < 1) {
            return;
        }

        let self = this;

        self.logger.debug(
            'Manage.RequestStopRenewingMemberships',
            {
                membershipIDs: self.membershipService.mapMembershipIds(memberships),
            }
        );

        let autoRenewingMemberships = self.membershipService.extractAutoRenewingMemberships(memberships);
        let notAutoRenewingMemberships = $(memberships).not(autoRenewingMemberships).get();

        let changingRefs = this.membershipService.generateMembershipRefsString(autoRenewingMemberships);

        let confirmStr = '<p>' + this.$filter('lang')('MANAGE_ACCOUNT.CONFIRM_STOP_RENEWING', {
            refs: changingRefs,
        }) + '</p>';

        if (notAutoRenewingMemberships.length > 0) {
            let notChangingRefs = this.membershipService.generateMembershipRefsString(notAutoRenewingMemberships);
            confirmStr += '<p>' + this.$filter('lang')('MANAGE_ACCOUNT.CONFIRM_STOP_RENEWING_UNCHANGED', {
                refs: notChangingRefs,
            }) + '</p>';
        }

        this.dialogService
            .confirm(
                '',
                {
                    customHtml: confirmStr,
                }
            )
            .then((confirmed) => {
                if (confirmed) {
                    self.membershipService.stopRenewingMemberships(
                        autoRenewingMemberships,
                        function () {
                            let stoppedMemberships = autoRenewingMemberships.filter(
                                function (m) {
                                    return m.autoRenew === false;
                                }
                            );

                            let notStoppedMemberships = autoRenewingMemberships.filter(
                                function (m) {
                                    return m.autoRenew !== false;
                                }
                            );

                            let stoppedStr = '';
                            if (stoppedMemberships.length > 0) {
                                let stoppedRefs = self.membershipService.generateMembershipRefsString(stoppedMemberships);
                                stoppedStr = self.$filter('lang')('MANAGE_ACCOUNT.STOPPED_RENEWING_ALERT', {
                                    refs: stoppedRefs,
                                });
                            }

                            let notStoppedStr = '';
                            if (notStoppedMemberships.length > 0) {
                                let notStoppedRefs = self.membershipService.generateMembershipRefsString(notStoppedMemberships);
                                notStoppedStr = self.$filter('lang')('MANAGE_ACCOUNT.NOT_STOPPED_RENEWING_ALERT', {
                                    refs: notStoppedRefs,
                                });
                                self.toastFactory.errorTop(
                                    null,
                                    notStoppedStr + ' ' + stoppedStr,
                                );
                            } else {
                                // Just show the ones that were stopped.
                                self.toastFactory.successTop(
                                    null,
                                    stoppedStr
                                );
                            }

                            callback(true);
                        }
                    );
                } else {
                    // Modal not confirmed.
                    callback(false);
                }
            });
    },

    /**
     * Show the modal to select a date to renew a membership or to clear that the user wishes to renew it.
     *
     * @param {Membership} membership
     * @param {boolean} multipleAllowed Can multiple memberships be set to renew from the current
     *                                  screen (order details) or only one (membership details)?
     *                                  This only affects the message that is shown after the modal is submitted.
     */
    changeMembershipRenewingState: function (membership, multipleAllowed) {
        // Do not allow more than one renewal of *the same membership* at once.
        // Check if there is already a renewal for this membership in the cart.
        const currentRenewingMembership = this.cartService.currentCart.getItems().find((i) => {
            return (i.renewingMembership && i.renewingMembership.getID() === membership.getID());
        });

        if (currentRenewingMembership) {
            this.toastFactory.errorTop(
                'MEMBERSHIPS.ERROR__RENEWAL_IN_PROGRESS',
            );
            return;
        }

        this.openRenewalModal(membership, multipleAllowed);
    },

    /**
     * Show the renewal modal.
     *
     * @param {Membership} membership
     * @param {boolean} multipleAllowed
     */
    openRenewalModal: function (membership, multipleAllowed) {
        if (!membership.renewalPlan.branchIDs.includes(this.userService.getItemBranchID())) {
            // This membership plan is not available in the currently selected item branch.
            // The user needs to switch item branch to one that does contain the plan.

            // Get a list of all the branch names (that the user has access to) that contain the plan.
            let branchNames = [];
            membership.renewalPlan.branchIDs.forEach((branchID) => {
                const branch = this.userService.getBranchByID(branchID);
                if (branch) {
                    branchNames.push(branch.name);
                }
            });

            // If the user does not have access to any branches that contain this membership plan then
            // we cannot continue with this renewal.
            if (branchNames.length < 1) {
                this.toastFactory.error(
                    'MEMBERSHIPS.RENEWAL_PLAN_NOT_IN_BRANCH_TITLE',
                    this.langService.getText(
                        'MEMBERSHIPS.RENEWAL_PLAN_NOT_IN_BRANCH_NONE',
                        {
                            membershipRef: membership.getRef(),
                            planName: membership.renewalPlan.name,
                        }
                    ),
                );

                return;
            }

            // At this point we know the plan is not in the currently selected item branch,
            // but there is at least one branch the user has access to that does contain this plan.
            // Prompt them to switch to a suitable branch.

            // The renewal plan for this membership is not available in the currently
            // selected order branch. For now we will show a warning that the user needs
            // to change the branch.
            //
            // This is checking the order branch not the item branch because until PROP-853 the renewal cart
            // items are generated using the order branch as the branch.
            //
            // TODO: Fix this in https://digitickets.atlassian.net/browse/PROP-853
            const messageKey = branchNames.length === 1
                ? 'MEMBERSHIPS.RENEWAL_PLAN_NOT_IN_BRANCH_ONE'
                : 'MEMBERSHIPS.RENEWAL_PLAN_NOT_IN_BRANCH_SOME';

            this.toastFactory.error(
                'MEMBERSHIPS.RENEWAL_PLAN_NOT_IN_BRANCH_TITLE',
                this.langService.getText(
                    messageKey,
                    {
                        membershipRef: membership.getRef(),
                        planName: membership.renewalPlan.name,
                        branchNames: branchNames.join(', '),
                    }
                ),
            );
            return;
        }

        this.renewalDateModalOpen = true;
        this.vueModalFactory
            .open(
                MODAL_NAMES__EPOS.MEMBERSHIP_RENEWAL_DATE,
                {
                    membership,
                }
            )
            .then(({ type, date }) => {
                membership.setRenewalOption(type, date);

                if (membership.isRenewing()) {
                    if (multipleAllowed) {
                        // Order details (potentially multiple memberships).
                        this.toastFactory.success('MEMBERSHIPS.RENEWAL_SELECTED_ALERT_MULTIPLE', membership.getRef());
                    } else {
                        // Membership details (single membership).
                        this.toastFactory.success('MEMBERSHIPS.RENEWAL_SELECTED_ALERT', membership.getRef());
                    }
                } else {
                    this.toastFactory.info('MEMBERSHIPS.RENEWAL_DESELECTED_ALERT', membership.getRef());
                }
            })
            .catch(suppressModalCancelledErrorOrReThrow)
            .finally(() => {
                this.renewalDateModalOpen = false;
                updateAngularUi();
            });
    },

    /**
     * Send the user to the Sell screen to take payments for renewing one of more memberships.
     *
     * @param {Membership[]} renewingMemberships
     */
    takePaymentForRenewingMemberships: async function takePaymentForRenewingMemberships(renewingMemberships) {
        // Take payment for the set of memberships we've been given.

        // Just check that it's not an empty set.
        if (renewingMemberships.length < 1) {
            return;
        }

        const cart = this.cartService.createEmptyCart();

        // Because they might have renewed the membership, and therefore all the data might be different (eg
        // whether they can renew or not, expiry dates), we set a flag in the path to force the order controller
        // to ignore any cached values and ask for a new copy of the order from the server. The membership
        // controller always asks for a fresh copy, so the flag is ignored there.
        cart.sellState.cancelCartReturnPath = new AppPath(
            this.$location.path(),
            { r: true, renewalSuccess: false }
        );

        cart.sellState.successReturnPath = new AppPath(
            this.$location.path(),
            { r: true, renewalSuccess: true }
        );

        cart.sellState.defaultItemTypeTab = ItemType.MEMBERSHIP_PLAN;

        for (let i = 0; i < renewingMemberships.length; i++) {
            let renewingMembership = renewingMemberships[i];

            // Set the customer account from the first renewing membership's account.
            if (!cart.customer.account && renewingMembership.customerAccount !== null) {
                cart.customer.account = renewingMembership.customerAccount;
            }

            const cartItem = new CartLine(
                null,
                renewingMembership.renewalPlan,
                // Use the order branch because renewing memberships happens off of the sell page where the item
                // branch can be selected.
                this.userService.currentBranch
            );
            cartItem.adjustQuantity(1);
            cartItem.canAdjustQuantity = false;
            cartItem.setRenewingMembership(renewingMembership, renewingMembership.getChosenRenewalFromDate());
            cartItem.setMembershipDatasets([renewingMembership.toMembershipDataset()]);

            cart.addItem(cartItem);

            renewingMembership.resetRenew();
        }

        const cartSet = await this.cartService.setNewCart(cart, true);
        if (cartSet) {
            this.navigationService.viewSell();
        }
    },
};

module.exports = MembershipManagementService;
